const iconModules = import.meta.glob(
  '../../../assets/Icons/Status Icons/*.{png,jpg,jpeg,svg}',
  { eager: true }
);

const customIconModules = import.meta.glob(
  '../../../assets/Status/**/*.{png,jpg,jpeg,svg}',
  { eager: true }
);

/**
 * Map file names to display names for dropdown
 */
const FILE_TO_DISPLAY_NAME_MAP: Record<string, string> = {
  'Step Completed': 'Step Complete',
  'Ready Review': 'Ready for Review',
  Ready_Review: 'Ready for Review',
  ready_review: 'Ready for Review',
};

/**
 * Map display names to file names for icon lookup
 */
const DISPLAY_TO_FILE_NAME_MAP: Record<string, string> = {
  'Step Complete': 'Step Completed',
  'Ready for Review': 'Ready Review',
};

/**
 * Convert file name to display name for dropdown
 * @param fileName -To display Filename
 * @returns - To Display filename
 */
const fileToDisplayName = (fileName: string): string => {
  if (FILE_TO_DISPLAY_NAME_MAP[fileName]) {
    return FILE_TO_DISPLAY_NAME_MAP[fileName];
  }

  const lowerFileName = fileName.toLowerCase();
  for (const [key, value] of Object.entries(FILE_TO_DISPLAY_NAME_MAP)) {
    if (key.toLowerCase() === lowerFileName) {
      return value;
    }
  }

  return fileName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Convert display name to file name for icon lookup
 * @param displayName - To Display the Filename
 * @returns - To Display file name
 */
const displayToFileName = (displayName: string): string => {
  return DISPLAY_TO_FILE_NAME_MAP[displayName] || displayName;
};

/**
 * Returns all unique state names based on available icons
 * @param size - To Display Sizes
 * @returns - To return Size
 */
export const getDynamicStates = (size: 'Regular' | 'Small'): string[] => {
  const suffix = size === 'Small' ? '_small' : '_regular';

  const states = Object.keys(iconModules)
    .filter((file) => {
      const filename = file.toLowerCase().replace(/^.*[\\/]/, '');
      const fileNameWithoutExt = filename.replace(/\.\w+$/, '');
      return fileNameWithoutExt.endsWith(suffix);
    })
    .map((filePath) => {
      const filename = filePath.replace(/^.*[\\/]/, '');
      const baseName = filename.replace(/\.\w+$/, '');

      const withoutSuffix = baseName.replace(/_regular|_small/i, '');
      return fileToDisplayName(withoutSuffix);
    });

  const ALL_STATES = [
    'AI',
    'Cleared',
    'Completed',
    'Downloaded',
    'Error',
    'Future',
    'In Progress',
    'Loading',
    'Not Started',
    'Not Started - Open',
    'Ready',
    'Ready for Review',
    'Revisit',
    'Shared',
    'Shared Open',
    'Step Complete',
    'Warning',
  ];

  const filteredStates = states.filter((state) => ALL_STATES.includes(state));
  return filteredStates.length > 0 ? filteredStates.sort() : ALL_STATES.sort();
};

/**
 * Returns the icon path for a given status and size
 * @param state
 * @param size
 * @returns - To Return Size
 */
export const getStateIconPath = (state: string, size: 'Regular' | 'Small'): string | null => {
  const suffix = size === 'Small' ? '_small' : '_regular';
  const fileState = displayToFileName(state);

  const cleanState = fileState
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

  const entry = Object.entries(iconModules).find(([path]) => {
    const filename = path.toLowerCase().replace(/^.*[\\/]/, '');
    const fileNameWithoutExt = filename.replace(/\.\w+$/, '');
    const normalizedFile = fileNameWithoutExt.toLowerCase().replace(/\s+/g, '_');
    const hasState = normalizedFile.startsWith(cleanState);
    const hasSuffix = normalizedFile.endsWith(suffix);
    return hasState && hasSuffix;
  });

  if (!entry) return null;

  const moduleValue = entry[1];
  if (typeof moduleValue === 'string') return moduleValue;
  if (moduleValue && typeof moduleValue === 'object' && 'default' in moduleValue)
    return moduleValue.default as string;

  return null;
};

/**
 * Returns all available status icons
 * @returns - To Return Icon Names
 */
export const getAllStatusIconsFromAssets = (): string[] => {
  const iconNames = new Set<string>();

  Object.keys(customIconModules).forEach((path) => {
    const filename = path.replace(/^.*[\\/]/, '');
    const match = filename.match(/Status Icon=([^,]+),/);
    if (match && match[1]) {
      const fileIconName = match[1].trim();
      const displayName = fileToDisplayName(fileIconName);
      iconNames.add(displayName);
    }
  });

  return Array.from(iconNames).sort((a, b) => a.localeCompare(b));
};

/**
 * Normalize icon type for comparison - handles different formats
 * @param iconType - To return Icon
 * @returns lowerType - To Display lowerType
 */
const normalizeIconType = (iconType: string): string => {
  const lowerType = iconType.toLowerCase().trim();

  // Handle "Status - Big" variations
  if (lowerType.includes('big')) {
    return 'status-big';
  }

  // Handle "Nav Status" variations
  if (lowerType.includes('nav')) {
    return 'nav-status';
  }

  // Handle other types
  return lowerType.replace(/\s+/g, '-');
};

/**
 * Normalize icon name for comparison
 * @param iconName -  To Display Icon Name
 * @returns iconName  -  To Display Icon Name
 */
const normalizeIconName = (iconName: string): string => {
  return iconName.toLowerCase().trim().replace(/\s+/g, '-');
};

const normalizeLooseText = (value: string): string => {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const containsTokensInOrder = (text: string, tokens: string[]): boolean => {
  let cursor = 0;

  for (const token of tokens) {
    const normalizedToken = normalizeLooseText(token);

    if (!normalizedToken) {
      continue;
    }

    const foundAt = text.indexOf(normalizedToken, cursor);
    if (foundAt === -1) {
      return false;
    }

    cursor = foundAt + normalizedToken.length;
  }

  return true;
};

/**
 * Parse filename to extract icon name and type
 * @param  filename - To display Filename
 * @returns  -  To return null
 */
const parseFilename = (filename: string): { iconName: string; iconType: string } | null => {
  // Remove extension
  const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');

  // Pattern 1: "Status Icon=Step Complete, Type=Status-Big"
  const pattern1 = /Status Icon=([^,]+),\s*Type=([^,]+)/i;

  // Pattern 2: "Status Icon=Step Complete, Status-Big"
  const pattern2 = /Status Icon=([^,]+),\s*([^,]+)/i;

  // Pattern 3: "Step Complete, Type=Status-Big"
  const pattern3 = /([^,]+),\s*Type=([^,]+)/i;

  // Pattern 4: "Step Complete, Status-Big"
  const pattern4 = /([^,]+),\s*([^,]+)/i;

  let match;

  if ((match = nameWithoutExt.match(pattern1))) {
    return { iconName: match[1].trim(), iconType: match[2].trim() };
  } else if ((match = nameWithoutExt.match(pattern2))) {
    return { iconName: match[1].trim(), iconType: match[2].trim() };
  } else if ((match = nameWithoutExt.match(pattern3))) {
    return { iconName: match[1].trim(), iconType: match[2].trim() };
  } else if ((match = nameWithoutExt.match(pattern4))) {
    return { iconName: match[1].trim(), iconType: match[2].trim() };
  }

  return null;
};

/**
 * Returns the icon path for a given status icon and icon type
 * @param  icon - To display icon
 * @param  iconType - To display iconType
 *  @returns  -  To return isNav
 */
export const getStatusIconPath = (icon: string, iconType: string): string | null => {
  // Convert display name to file name
  const fileIcon = displayToFileName(icon);

  console.log(`🔍 Looking for icon: "${icon}" (as "${fileIcon}") with type: "${iconType}"`);

  // Normalize inputs
  const normalizedIcon = normalizeIconName(fileIcon);
  const normalizedType = normalizeIconType(iconType);

  const isBigIcon = normalizedType === 'status-big';
  const isNavStatus = normalizedType === 'nav-status';

  console.log(`   Normalized: icon="${normalizedIcon}", type="${normalizedType}"`);
  console.log(`   Is Big Icon: ${isBigIcon}, Is Nav Status: ${isNavStatus}`);

  // Get all entries
  const entries = Object.entries(customIconModules);

  // STRATEGY 1: First, try to find exact matches for the specific type
  console.log(`\n📋 STRATEGY 1: Looking for exact ${iconType} matches...`);

  for (const [path, moduleValue] of entries) {
    const filename = path.replace(/^.*[\\/]/, '');
    const parsed = parseFilename(filename);

    if (!parsed) continue;

    const parsedIcon = normalizeIconName(parsed.iconName);
    const parsedType = normalizeIconType(parsed.iconType);

    const iconMatches = parsedIcon === normalizedIcon;
    const typeMatches = parsedType === normalizedType;

    if (iconMatches && typeMatches) {
      console.log(`✅ FOUND EXACT MATCH: ${filename}`);
      console.log(`   Parsed: icon="${parsed.iconName}", type="${parsed.iconType}"`);

      if (typeof moduleValue === 'string') return moduleValue;
      if (moduleValue && typeof moduleValue === 'object' && 'default' in moduleValue)
        return moduleValue.default as string;
    }
  }

  // STRATEGY 2: For "Status - Big", look for any big icon with matching name
  if (isBigIcon) {
    console.log(`\n📋 STRATEGY 2: Looking for Status - Big variants...`);

    const bigEntries = entries.filter(([path]) => {
      // const filename = path.toLowerCase();
      const parsed = parseFilename(path.replace(/^.*[\\/]/, ''));

      if (!parsed) return false;

      const parsedType = normalizeIconType(parsed.iconType);
      const isBig = parsedType === 'status-big' || parsed.iconType.toLowerCase().includes('big');

      return isBig;
    });

    console.log(`   Found ${bigEntries.length} big icon files`);

    // Now find the one with matching icon name
    for (const [path, moduleValue] of bigEntries) {
      const filename = path.replace(/^.*[\\/]/, '');
      const parsed = parseFilename(filename);

      if (!parsed) continue;

      const parsedIcon = normalizeIconName(parsed.iconName);
      const iconMatches =
        parsedIcon === normalizedIcon ||
        parsedIcon.includes(normalizedIcon) ||
        normalizedIcon.includes(parsedIcon);

      if (iconMatches) {
        console.log(`✅ FOUND BIG ICON: ${filename}`);
        console.log(`   Parsed: icon="${parsed.iconName}", type="${parsed.iconType}"`);

        if (typeof moduleValue === 'string') return moduleValue;
        if (moduleValue && typeof moduleValue === 'object' && 'default' in moduleValue)
          return moduleValue.default as string;
      }
    }
  }

  // STRATEGY 3: For "Nav Status", look for nav status icons
  if (isNavStatus) {
    console.log(`\n📋 STRATEGY 3: Looking for Nav Status variants...`);

    const navEntries = entries.filter(([path]) => {
      // const filename = path.toLowerCase();
      const parsed = parseFilename(path.replace(/^.*[\\/]/, ''));

      if (!parsed) return false;

      const parsedType = normalizeIconType(parsed.iconType);
      const isNav = parsedType === 'nav-status' || parsed.iconType.toLowerCase().includes('nav');

      return isNav;
    });

    console.log(`   Found ${navEntries.length} nav status files`);

    // Now find the one with matching icon name
    for (const [path, moduleValue] of navEntries) {
      const filename = path.replace(/^.*[\\/]/, '');
      const parsed = parseFilename(filename);

      if (!parsed) continue;

      const parsedIcon = normalizeIconName(parsed.iconName);
      const iconMatches =
        parsedIcon === normalizedIcon ||
        parsedIcon.includes(normalizedIcon) ||
        normalizedIcon.includes(parsedIcon);

      if (iconMatches) {
        console.log(`✅ FOUND NAV STATUS ICON: ${filename}`);
        console.log(`   Parsed: icon="${parsed.iconName}", type="${parsed.iconType}"`);

        if (typeof moduleValue === 'string') return moduleValue;
        if (moduleValue && typeof moduleValue === 'object' && 'default' in moduleValue)
          return moduleValue.default as string;
      }
    }
  }

  // STRATEGY 4: Try pattern matching as fallback
  console.log(`\n📋 STRATEGY 4: Trying pattern matching...`);

  const getFilename = (path: string): string => path.replace(/^.*[\\/]/, '');

  const findByTokenSequences = (sequences: string[][]): [string, unknown] | undefined => {
    for (const sequence of sequences) {
      console.log(`   Trying token sequence: ${sequence.join(' -> ')}`);

      const entry = entries.find(([path]) => {
        const normalizedFilename = normalizeLooseText(getFilename(path));
        return containsTokensInOrder(normalizedFilename, sequence);
      });

      if (entry) {
        return entry;
      }
    }

    return undefined;
  };

  let fallbackEntry: [string, unknown] | undefined;

  if (isBigIcon) {
    fallbackEntry = findByTokenSequences([
      [fileIcon, 'status', 'big'],
      [fileIcon, 'big'],
      ['big', fileIcon],
      ['status', fileIcon, 'big'],
    ]);

    // Keep a dedicated broad fallback for Step Complete naming variants.
    if (!fallbackEntry && normalizedIcon.includes('step') && normalizedIcon.includes('complete')) {
      fallbackEntry = findByTokenSequences([
        ['step', 'complete', 'big'],
        ['step', 'completed', 'big'],
        ['big', 'step', 'complete'],
        ['big', 'step', 'completed'],
      ]);
    }
  } else if (isNavStatus) {
    fallbackEntry = findByTokenSequences([
      [fileIcon, 'nav'],
      ['nav', fileIcon],
      [fileIcon, 'nav', 'status'],
    ]);
  } else {
    fallbackEntry = findByTokenSequences([
      [fileIcon, iconType],
      [iconType, fileIcon],
    ]);
  }

  if (fallbackEntry) {
    const filename = getFilename(fallbackEntry[0]);
    console.log(`✅ FOUND WITH TOKEN FALLBACK: ${filename}`);

    const moduleValue = fallbackEntry[1];
    if (typeof moduleValue === 'string') return moduleValue;
    if (moduleValue && typeof moduleValue === 'object' && 'default' in moduleValue)
      return moduleValue.default as string;
  }

  // STRATEGY 5: Last resort - find any matching icon (but avoid wrong type)
  console.log(`\n📋 STRATEGY 5: Last resort - avoiding wrong types...`);

  // For big icons, ONLY look for files containing "big"
  if (isBigIcon) {
    const bigOnlyEntries = entries.filter(([path]) => {
      const filename = path.toLowerCase();
      return filename.includes('big');
    });

    console.log(`   Filtered to ${bigOnlyEntries.length} files containing "big"`);

    for (const [path, moduleValue] of bigOnlyEntries) {
      const filename = path.toLowerCase();
      if (filename.includes(normalizedIcon.replace(/-/g, ''))) {
        console.log(`✅ FOUND IN BIG FILES: ${path.replace(/^.*[\\/]/, '')}`);

        if (typeof moduleValue === 'string') return moduleValue;
        if (moduleValue && typeof moduleValue === 'object' && 'default' in moduleValue)
          return moduleValue.default as string;
      }
    }
  }

  // For nav status, ONLY look for files containing "nav"
  if (isNavStatus) {
    const navOnlyEntries = entries.filter(([path]) => {
      const filename = path.toLowerCase();
      return filename.includes('nav');
    });

    console.log(`   Filtered to ${navOnlyEntries.length} files containing "nav"`);

    for (const [path, moduleValue] of navOnlyEntries) {
      const filename = path.toLowerCase();
      if (filename.includes(normalizedIcon.replace(/-/g, ''))) {
        console.log(`✅ FOUND IN NAV FILES: ${path.replace(/^.*[\\/]/, '')}`);

        if (typeof moduleValue === 'string') return moduleValue;
        if (moduleValue && typeof moduleValue === 'object' && 'default' in moduleValue)
          return moduleValue.default as string;
      }
    }
  }

  console.warn(`❌ Icon not found: "${icon}" with type "${iconType}"`);
  console.warn(`   Searched as: "${fileIcon}" (normalized: "${normalizedIcon}")`);
  console.warn(`   Available files:`, entries.map(([p]) => p.replace(/^.*[\\/]/, '')).slice(0, 10));

  return null;
};

/**
 * Debug function to see all available icon files
 *  @returns  -  To return null
 */
export const getAllCustomIconFiles = (): string[] => {
  return Object.keys(customIconModules).map((path) => path.replace(/^.*[\\/]/, ''));
};

/**
 * Debug function to analyze icon files by type
 */
export const analyzeIconFilesByType = (): void => {
  console.log('=== ICON FILE ANALYSIS ===');

  const entries = Object.entries(customIconModules);
  const byType: Record<string, string[]> = {};

  entries.forEach(([path]) => {
    const filename = path.replace(/^.*[\\/]/, '');
    const parsed = parseFilename(filename);

    if (parsed) {
      const type = normalizeIconType(parsed.iconType);
      if (!byType[type]) byType[type] = [];
      byType[type].push(`${parsed.iconName} -> ${filename}`);
    } else {
      if (!byType['unknown']) byType['unknown'] = [];
      byType['unknown'].push(filename);
    }
  });

  // Display by type
  Object.entries(byType).forEach(([type, files]) => {
    console.log(`\n📁 ${type.toUpperCase()} (${files.length} files):`);
    files.forEach((file) => console.log(`   - ${file}`));
  });

  console.log('==========================');
};

// Run analysis in development
if (import.meta.env.DEV) {
  setTimeout(() => {
    analyzeIconFilesByType();
  }, 1000);
}

